/**
 * @private
 */
const _removeItem = (keyName, useSession = false) => {
  if (typeof window === 'undefined' || !keyName) {
    return false
  }

  // need try-catch to handle "SecurityError: The operation is insecure." if cookies are disabled
  try {
    if (useSession) {
      window.sessionStorage.removeItem(keyName)
    } else {
      window.localStorage.removeItem(keyName)
    }

    return true
  } catch (e) {
    return false
  }
}

/**
 * @private
 */
const _getItem = (keyName, useSession = false) => {
  if (typeof window === 'undefined' || !keyName) {
    return null
  }

  // need try-catch to handle "SecurityError: The operation is insecure." if cookies are disabled
  try {
    const storageData = useSession ? sessionStorage.getItem(keyName) : localStorage.getItem(keyName)

    if (!storageData) {
      return null
    }

    return JSON.parse(decodeURIComponent(storageData))
  } catch (e) {
    if (!!window.sessionStorage.getItem(keyName)) {
      _removeItem(keyName)
    }
    return null
  }
}

/**
 * @private
 */
const _setItem = (keyName, data, useSession) => {
  if (typeof window === 'undefined' || !keyName || !data) {
    return false
  }

  useSession = useSession ? useSession : false

  // need try-catch to handle "SecurityError: The operation is insecure." if cookies are disabled
  try {
    const storageData = encodeURIComponent(JSON.stringify(data))

    if (useSession) {
      sessionStorage.setItem(keyName, storageData)
    } else {
      localStorage.setItem(keyName, storageData)
    }

    return true
  } catch (e) {
    return false
  }
}

/**
 * @private
 */
const _updateItem = (keyName, data, useSession) => {
  useSession = useSession ? useSession : false

  const currentData = _getItem(keyName, useSession)
  const updatedData = { ...(currentData ? currentData : {}), ...data }

  return _setItem(keyName, updatedData, useSession)
}

/**
 * Remove session-storage-data for an item.
 *
 * @param keyName the name to access the storage-data
 *
 * @returns 'true' when successfully removed otherwise 'false'
 */
export const removeSessionItem = (keyName) => {
  return _removeItem(keyName, true)
}

/**
 * Returns the session-storage-data for an item.
 *
 * @param keyName the name to access the storage-data
 *
 * @returns the storage data. if no data is accessible 'null' or 'undefined' will be returned.
 */
export const getSessionItem = (keyName) => {
  return _getItem(keyName, true)
}

/**
 * Saves the data to session-storage.
 *
 * @param keyName the name to access the storage-data
 * @param data value to store
 *
 * @returns 'true' on success otherwise 'false'
 */

export const setSessionItem = (keyName, data) => {
  return _setItem(keyName, data, true)
}

/**
 * Extend the existing session-data.
 * If no session-data exist save data only.
 *
 * @param keyName the name to access the storage-data
 * @param data to store/update
 *
 * @returns 'true' on success otherwise 'false'
 */
export const updateSessionItem = (keyName, data) => {
  return _updateItem(keyName, data, true)
}

/**
 * Remove local-storage-data for an item.
 *
 * @param keyName the name to access the storage-data
 * @returns 'true' when successfully removed otherwise 'false'
 */
export const removeLocalItem = (keyName) => {
  return _removeItem(keyName)
}

/**
 * Returns the local-storage-data for an item.
 *
 * @param keyName the name to access the storage-data
 *
 * @returns the storage data. if no data is accessible 'null' or 'undefined' will be returned.
 */
export const getLocalItem = (keyName) => {
  return _getItem(keyName)
}

/**
 * Saves the data to local-storage.
 *
 * @param keyName the name to access the storage-data
 * @param data value to store
 *
 * @returns 'true' on success otherwise 'false'
 */
export const setLocalItem = (keyName, data) => {
  return _setItem(keyName, data)
}

/**
 * Extend the existing session-data.
 * If no session-data exist save data only.
 *
 * @param keyName the name to access the storage-data
 * @param data to store/update
 *
 * @returns 'true' on success otherwise 'false'
 */
export const updateLocalItem = (keyName, data) => {
  return _updateItem(keyName, data)
}
